import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Trash2,
  Globe,
  MessageCircle,
  MapPin,
  ChevronDown,
  ChevronRight,
  Zap,
  Users,
  MessageSquare
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Conversation Row Component
const ConversationRow = ({ conversation, onViewDetails }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="border-b border-gray-700/50 last:border-b-0 p-4 hover:bg-gray-700/30 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              conversation.role === 'professor' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
            }`}>
              {conversation.role === 'professor' ? 'Profesor' : 'Alumno'}
            </span>
            <span className="text-xs text-gray-500">{conversation.subject_id}</span>
            <span className="text-xs text-gray-500">•</span>
            <span className="text-xs text-gray-500">{conversation.message_count} mensajes</span>
          </div>
          {conversation.first_message_preview && (
            <p className="text-sm text-gray-300 truncate mb-1">
              <span className="text-blue-400 font-medium">P:</span> {conversation.first_message_preview}
            </p>
          )}
          {conversation.last_message_preview && (
            <p className="text-sm text-gray-400 truncate">
              <span className="text-green-400 font-medium">R:</span> {conversation.last_message_preview}
            </p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-gray-400">{formatDate(conversation.updated_at)}</p>
          <button
            onClick={() => onViewDetails(conversation.conversation_id)}
            className="mt-2 px-3 py-1.5 text-xs bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 rounded-lg transition-colors border border-blue-500/30"
          >
            Ver completa
          </button>
        </div>
      </div>
    </div>
  );
};

// Conversation Detail Modal
const ConversationDetailModal = ({ conversationId, pin, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [subjectId, setSubjectId] = useState(null);

  useEffect(() => {
    const fetchConversation = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/analytics/conversations?pin=${pin}&conversation_id=${conversationId}`);
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
          setSubjectId(data.subject_id);
        }
      } catch (err) {
        console.error('Error fetching conversation:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchConversation();
  }, [conversationId, pin]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-gray-900 rounded-xl border border-gray-700 max-w-2xl w-full max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium">Conversación Completa</h3>
            <p className="text-xs text-gray-400">{subjectId} • {messages.length} mensajes</p>
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white text-xl px-2"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No hay mensajes</p>
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-100 border border-gray-700'
                }`}>
                  <p className="text-xs text-gray-400 mb-1">
                    {msg.role === 'user' ? '👤 Usuario' : '🤖 Milo'}
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Location Map Component
const LocationMap = ({ locations }) => {
  if (!locations || locations.length === 0) {
    return (
      <div className="h-72 bg-gray-800/50 rounded-xl flex items-center justify-center border border-gray-700/50">
        <div className="text-center text-gray-400">
          <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="font-medium">No hay datos de ubicación aún</p>
          <p className="text-xs mt-1 text-gray-500">Las ubicaciones se registran automáticamente</p>
          <p className="text-xs text-gray-500">cuando los usuarios usan el chat</p>
        </div>
      </div>
    );
  }

  // Calculate center from locations
  const avgLat = locations.reduce((sum, loc) => sum + loc.latitude, 0) / locations.length;
  const avgLng = locations.reduce((sum, loc) => sum + loc.longitude, 0) / locations.length;

  // Group locations by city for counting
  const cityCounts = locations.reduce((acc, loc) => {
    const key = loc.city || 'Desconocido';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="h-72 rounded-xl overflow-hidden border border-gray-700/50">
        <MapContainer
          center={[avgLat, avgLng]}
          zoom={3}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          {locations.map((loc, i) => (
            <Marker key={i} position={[loc.latitude, loc.longitude]} icon={customIcon}>
              <Popup>
                <div className="text-sm">
                  <p className="font-medium">{loc.city || 'Ubicación'}</p>
                  <p className="text-gray-500">{loc.country || ''}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(loc.timestamp).toLocaleString('es-ES')}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      
      {/* Location Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Object.entries(cityCounts).slice(0, 6).map(([city, count]) => (
          <div key={city} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg border border-gray-700/50">
            <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <span className="text-xs text-gray-300 truncate flex-1">{city}</span>
            <span className="text-xs font-medium text-white">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Analytics Panel Component
const AnalyticsPanel = ({ pin }) => {
  const [locations, setLocations] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('map');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    if (!pin) return;
    
    try {
      setIsRefreshing(true);
      
      // Fetch locations
      const locationsRes = await fetch(`${API_BASE}/api/analytics/locations?pin=${pin}&limit=100`);
      if (locationsRes.ok) {
        const locationsData = await locationsRes.json();
        setLocations(locationsData.locations || []);
      }
      
      // Fetch conversations
      const convoRes = await fetch(`${API_BASE}/api/analytics/conversations?pin=${pin}&limit=50`);
      if (convoRes.ok) {
        const convoData = await convoRes.json();
        setConversations(convoData.conversations || []);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Error al cargar los datos');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [pin]);

  // Initial load
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAnalytics]);

  // Clear data
  const handleClearData = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar todos los datos de analítica?')) {
      return;
    }
    
    try {
      await fetch(`${API_BASE}/api/analytics/clear?pin=${pin}&data_type=all`, { method: 'DELETE' });
      fetchAnalytics();
    } catch (err) {
      console.error('Error clearing data:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error}</p>
        <button 
          onClick={fetchAnalytics}
          className="mt-4 px-4 py-2 bg-gray-700 rounded-lg text-white hover:bg-gray-600 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800/50 rounded-lg border border-gray-700/50">
            <MapPin className="w-4 h-4 text-blue-400" />
            <span className="text-gray-300">{locations.length} ubicaciones</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800/50 rounded-lg border border-gray-700/50">
            <MessageSquare className="w-4 h-4 text-green-400" />
            <span className="text-gray-300">{conversations.length} conversaciones</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`p-2 rounded-lg transition-colors ${
              autoRefresh ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
            title={autoRefresh ? 'Auto-refresh activo (30s)' : 'Activar auto-refresh'}
          >
            <Zap className="w-4 h-4" />
          </button>
          <button
            onClick={fetchAnalytics}
            disabled={isRefreshing}
            className="p-2 bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors disabled:opacity-50"
            title="Actualizar datos"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleClearData}
            className="p-2 bg-red-500/20 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors border border-red-500/30"
            title="Limpiar todos los datos"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('map')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
            activeTab === 'map'
              ? 'bg-blue-600/20 text-blue-400 border border-blue-500/50'
              : 'bg-gray-800/50 text-gray-400 hover:text-white border border-gray-700/50 hover:border-gray-600/50'
          }`}
        >
          <Globe className="w-4 h-4" />
          <span className="text-sm font-medium">Mapa de Tráfico</span>
        </button>
        <button
          onClick={() => setActiveTab('conversations')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
            activeTab === 'conversations'
              ? 'bg-green-600/20 text-green-400 border border-green-500/50'
              : 'bg-gray-800/50 text-gray-400 hover:text-white border border-gray-700/50 hover:border-gray-600/50'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Conversaciones</span>
        </button>
      </div>

      {/* Map Tab */}
      {activeTab === 'map' && (
        <div className="bg-gray-800/30 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-blue-400" />
            <h4 className="text-white font-medium">Ubicaciones de Acceso</h4>
            <span className="text-xs text-gray-500 ml-auto">Geolocalización por IP (automática)</span>
          </div>
          <LocationMap locations={locations} />
        </div>
      )}

      {/* Conversations Tab */}
      {activeTab === 'conversations' && (
        <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 overflow-hidden">
          <div className="p-4 border-b border-gray-700/50">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-400" />
              <h4 className="text-white font-medium">Historial de Conversaciones</h4>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Todas las preguntas y respuestas de los usuarios
            </p>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No hay conversaciones registradas</p>
                <p className="text-xs text-gray-500 mt-1">Las conversaciones aparecerán aquí cuando los usuarios chateen</p>
              </div>
            ) : (
              conversations.map((conv, i) => (
                <ConversationRow
                  key={conv.conversation_id || i}
                  conversation={conv}
                  onViewDetails={setSelectedConversation}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Conversation Detail Modal */}
      {selectedConversation && (
        <ConversationDetailModal
          conversationId={selectedConversation}
          pin={pin}
          onClose={() => setSelectedConversation(null)}
        />
      )}
    </div>
  );
};

export default AnalyticsPanel;
